import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useNotificationSound } from "./useNotificationSound";

export const useUnreadCount = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { showPushNotification } = useNotificationSound();

  const { data: count = 0 } = useQuery({
    queryKey: ["unread-notifications", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);
      if (error) return 0;
      return count || 0;
    },
    enabled: !!user,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("notif-count")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        queryClient.invalidateQueries({ queryKey: ["unread-notifications"] });

        // Show push notification with sound for all new notifications
        const n = payload.new as any;
        if (n) {
          showPushNotification(
            n.title || "Ny notis",
            n.body || "",
            n.link || "/notifications"
          );
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient, showPushNotification]);

  return count;
};
